import React, { useEffect, useMemo, useRef, useState } from "react";
import API_BASE_URL from "../../config";

function getToken() {
  return localStorage.getItem("token");
}

function formatPlanPrice(value) {
  if (value === undefined || value === null || value === "") return "Custom";

  const numericValue = Number(value);

  if (Number.isNaN(numericValue)) return "Custom";

  return numericValue.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
}

function formatDateOnly(value) {
  if (!value) return "Not available";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "Not available";
  }

  return date.toLocaleDateString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric"
  });
}

function normalizeBoolean(value) {
  return value === true || value === 1 || value === "1";
}

function normalizeTierRank(value) {
  const rank = Number(value);

  if (!Number.isInteger(rank) || rank <= 0) {
    return null;
  }

  return rank;
}

function normalizeBillingFrequency(value) {
  const frequency = String(value || "").trim().toLowerCase();

  if (frequency === "annual" || frequency === "yearly" || frequency === "year") {
    return "annual";
  }

  return "monthly";
}

function getPlanPriceLabel(plan, billingFrequency) {
  if (!plan || normalizeBoolean(plan.is_custom_pricing)) {
    return "Custom pricing";
  }

  if (billingFrequency === "annual") {
    return `${formatPlanPrice(plan.annual_price)}/yr`;
  }

  return `${formatPlanPrice(plan.monthly_price)}/mo`;
}

function renderPopupMessage(title, message) {
  document.body.innerHTML = `
    <div style="font-family: Inter, Arial, sans-serif; padding: 32px; text-align: center;">
      <h2 style="margin: 0 0 8px; color: #0f172a;">${title}</h2>
      <p style="margin: 0; color: #64748b;">${message}</p>
    </div>
  `;
}

function SubscriptionPlanSettingsCard() {
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  const [subscriptionSaving, setSubscriptionSaving] = useState(false);
  const [billingPortalLoading, setBillingPortalLoading] = useState(false);
  const [subscriptionError, setSubscriptionError] = useState("");
  const [availablePlans, setAvailablePlans] = useState([]);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [selectedSubscriptionPlanId, setSelectedSubscriptionPlanId] = useState("");
  const [selectedBillingFrequency, setSelectedBillingFrequency] = useState("monthly");
  const [showUpgradeOptions, setShowUpgradeOptions] = useState(false);
  const [checkoutMessage, setCheckoutMessage] = useState("");

  const checkoutWindowRef = useRef(null);
  const checkoutPollRef = useRef(null);

  const currentPlanId =
    currentSubscription?.subscription_plan_id ||
    currentSubscription?.plan_id ||
    "";

  const currentPlanTierRank = normalizeTierRank(
    currentSubscription?.subscription_plan_tier_rank ||
      currentSubscription?.tier_rank
  );

  const currentPlanName =
    currentSubscription?.subscription_plan_name ||
    currentSubscription?.plan_name ||
    currentSubscription?.current_plan_name ||
    "Not assigned";

  const currentBillingFrequency = normalizeBillingFrequency(
    currentSubscription?.billing_frequency || "monthly"
  );

  const currentPlanPrice =
    currentSubscription?.subscription_plan_is_custom_pricing
      ? "Custom pricing"
      : currentBillingFrequency === "annual" &&
        currentSubscription?.subscription_plan_annual_price !== undefined &&
        currentSubscription?.subscription_plan_annual_price !== null
        ? `${formatPlanPrice(currentSubscription.subscription_plan_annual_price)}/yr`
        : currentSubscription?.subscription_plan_monthly_price !== undefined &&
          currentSubscription?.subscription_plan_monthly_price !== null
          ? `${formatPlanPrice(currentSubscription.subscription_plan_monthly_price)}/mo`
          : currentSubscription?.monthly_amount
            ? `${formatPlanPrice(currentSubscription.monthly_amount)}/mo`
            : "Price not available";

  const currentPlanSummary =
    subscriptionLoading ? "Loading..." : `${currentPlanName} — ${currentPlanPrice}`;

  const hasStripeSubscription = Boolean(
    currentSubscription?.stripe_subscription_id
  );

  const showConnectBillingButton =
    Boolean(currentSubscription) &&
    !subscriptionLoading &&
    !hasStripeSubscription;

  const showBillingPortalButton =
    Boolean(currentSubscription) &&
    !subscriptionLoading &&
    hasStripeSubscription;

  const upgradeEligiblePlans = useMemo(() => {
    if (!currentPlanTierRank) return [];

    return availablePlans.filter((plan) => {
      const planTierRank = normalizeTierRank(plan.tier_rank);

      if (!planTierRank) return false;
      if (String(plan.id) === String(currentPlanId)) return false;

      return planTierRank < currentPlanTierRank;
    });
  }, [availablePlans, currentPlanId, currentPlanTierRank]);

  const selectedSubscriptionPlan = useMemo(() => {
    return (
      upgradeEligiblePlans.find(
        (plan) => String(plan.id) === String(selectedSubscriptionPlanId)
      ) || null
    );
  }, [upgradeEligiblePlans, selectedSubscriptionPlanId]);

  const selectedPlanPriceLabel = selectedSubscriptionPlan
    ? getPlanPriceLabel(selectedSubscriptionPlan, selectedBillingFrequency)
    : "";

  const hasUpgradeOptions = upgradeEligiblePlans.length > 0;

  const loadSubscriptionSettings = async () => {
    try {
      setSubscriptionLoading(true);
      setSubscriptionError("");

      const token = getToken();

      if (!token) return;

      const [plansRes, subscriptionRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/subscription-plans/public`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }),
        fetch(`${API_BASE_URL}/api/subscription-plans/my-subscription`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        })
      ]);

      const plansData = await plansRes.json();
      const subscriptionData = await subscriptionRes.json();

      if (!plansRes.ok) {
        throw new Error(
          plansData.message ||
          "Failed to load subscription plans"
        );
      }

      if (!subscriptionRes.ok) {
        throw new Error(
          subscriptionData.message ||
          "Failed to load current subscription"
        );
      }

      const plans = Array.isArray(plansData)
        ? plansData
        : Array.isArray(plansData.plans)
          ? plansData.plans
          : [];

      const subscription =
        subscriptionData.subscription ||
        subscriptionData.subscriber ||
        subscriptionData.account ||
        subscriptionData ||
        null;

      setAvailablePlans(plans);
      setCurrentSubscription(subscription);
      setSelectedSubscriptionPlanId("");
      setSelectedBillingFrequency(
        normalizeBillingFrequency(
          subscription?.billing_frequency ||
          "monthly"
        )
      );
    } catch (err) {
      console.error(
        "Load subscription settings error:",
        err
      );

      setSubscriptionError(
        err.message ||
        "Failed to load subscription settings"
      );
    } finally {
      setSubscriptionLoading(false);
    }
  };

  const syncCheckoutSession = async (sessionId) => {
    const token = getToken();

    if (!token) {
      throw new Error(
        "You must be logged in to sync checkout."
      );
    }

    const res = await fetch(
      `${API_BASE_URL}/api/stripe-billing/checkout-session/${encodeURIComponent(
        sessionId
      )}`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    );

    const data = await res.json();

    if (!res.ok) {
      throw new Error(
        data.message ||
        "Failed to sync Stripe Checkout Session"
      );
    }

    return data;
  };

  const syncCurrentStripeSubscription = async () => {
    const token = getToken();

    if (!token) {
      throw new Error(
        "You must be logged in to sync billing."
      );
    }

    const res = await fetch(
      `${API_BASE_URL}/api/stripe-billing/current/sync`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        }
      }
    );

    const data = await res.json();

    if (!res.ok) {
      throw new Error(
        data.message ||
        "Failed to sync Stripe subscription"
      );
    }

    return data;
  };

  const clearCheckoutPoll = () => {
    if (checkoutPollRef.current) {
      window.clearInterval(
        checkoutPollRef.current
      );

      checkoutPollRef.current = null;
    }
  };

  const handleCheckoutSuccessRefresh = async () => {
    clearCheckoutPoll();

    setCheckoutMessage(
      "Payment submitted successfully. Refreshing your subscription..."
    );

    setShowUpgradeOptions(false);
    setSelectedSubscriptionPlanId("");

    await loadSubscriptionSettings();

    window.setTimeout(() => {
      window.location.reload();
    }, 900);
  };

  const handleOpenBillingPortal = async () => {
    try {
      setBillingPortalLoading(true);
      setSubscriptionError("");
      setCheckoutMessage("");

      const token = getToken();

      if (!token) {
        alert(
          "You must be logged in."
        );

        return;
      }

      const returnUrl = new URL(
        window.location.href
      );

      returnUrl.searchParams.set(
        "billing",
        "portal-return"
      );

      returnUrl.searchParams.delete(
        "checkout"
      );

      returnUrl.searchParams.delete(
        "session_id"
      );

      returnUrl.searchParams.delete(
        "stripe_popup"
      );

      const res = await fetch(
        `${API_BASE_URL}/api/stripe-billing/current/portal`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({
            return_url:
              returnUrl.toString()
          })
        }
      );

      const data = await res.json();

      if (!res.ok) {
        throw new Error(
          data.message ||
          "Failed to open billing portal"
        );
      }

      if (!data.portal_url) {
        throw new Error(
          "Billing portal URL was not returned"
        );
      }

      window.location.href =
        data.portal_url;
    } catch (err) {
      console.error(
        "Open Stripe Billing Portal error:",
        err
      );

      setSubscriptionError(
        err.message ||
        "Failed to open billing portal"
      );

      alert(
        err.message ||
        "Failed to open billing portal"
      );
    } finally {
      setBillingPortalLoading(false);
    }
  };

  const handleConnectBilling = async () => {
    try {
      setSubscriptionSaving(true);
      setSubscriptionError("");
      setCheckoutMessage("");

      const token = getToken();

      if (!token) {
        throw new Error(
          "You must be logged in."
        );
      }

      if (!currentPlanId) {
        throw new Error(
          "Your current subscription plan could not be identified."
        );
      }

      const confirmed = window.confirm(
        `Start your ${currentPlanName} subscription at ${currentPlanPrice}?`
      );

      if (!confirmed) {
        return;
      }

      setCheckoutMessage(
        "Opening Stripe Checkout..."
      );

      const res = await fetch(
        `${API_BASE_URL}/api/stripe-billing/current/checkout`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({
            subscription_plan_id:
              currentPlanId,
            billing_frequency:
              currentBillingFrequency,
            licensed_user_count:
              Number(
                currentSubscription
                  ?.licensed_user_count ||
                1
              )
          })
        }
      );

      const data = await res.json();

      if (!res.ok) {
        throw new Error(
          data.message ||
          "Failed to start Stripe Checkout"
        );
      }

      if (!data.checkout_url) {
        throw new Error(
          "Stripe Checkout URL was not returned"
        );
      }

      window.location.href =
        data.checkout_url;
    } catch (err) {
      console.error(
        "Connect Stripe billing error:",
        err
      );

      setSubscriptionError(
        err.message ||
        "Failed to connect billing"
      );

      setCheckoutMessage("");

      alert(
        err.message ||
        "Failed to connect billing"
      );
    } finally {
      setSubscriptionSaving(false);
    }
  };

  useEffect(() => {
    loadSubscriptionSettings();

    const params = new URLSearchParams(
      window.location.search
    );

    const billingStatus =
      params.get("billing");

    const checkoutStatus =
      params.get("checkout");

    const isStripePopup =
      params.get("stripe_popup") === "1";

    const sessionId =
      params.get("session_id");

    if (
      billingStatus ===
      "portal-return"
    ) {
      const handlePortalReturn = async () => {
        try {
          setCheckoutMessage(
            "Syncing Stripe billing changes..."
          );

          await syncCurrentStripeSubscription();
          await loadSubscriptionSettings();

          setCheckoutMessage(
            "Billing changes synced successfully."
          );
        } catch (err) {
          console.error(
            "Stripe portal return sync error:",
            err
          );

          setCheckoutMessage(
            "Returned from Stripe Billing Portal. Could not sync changes automatically."
          );

          setSubscriptionError(
            err.message ||
            "Failed to sync billing changes"
          );
        } finally {
          const cleanedUrl = new URL(
            window.location.href
          );

          cleanedUrl.searchParams.delete(
            "billing"
          );

          window.history.replaceState(
            {},
            document.title,
            `${cleanedUrl.pathname}${cleanedUrl.search}${cleanedUrl.hash}`
          );
        }
      };

      handlePortalReturn();
    }

    if (
      checkoutStatus ===
        "success" &&
      sessionId &&
      !isStripePopup
    ) {
      const handleCheckoutReturn = async () => {
        try {
          setCheckoutMessage(
            "Payment submitted successfully. Syncing your subscription..."
          );

          await syncCheckoutSession(
            sessionId
          );

          await loadSubscriptionSettings();

          setCheckoutMessage(
            "Your Stripe subscription is connected."
          );
        } catch (err) {
          console.error(
            "Stripe checkout return sync error:",
            err
          );

          setCheckoutMessage(
            "Payment was submitted. Your subscription will update automatically after Stripe finishes processing."
          );
        } finally {
          window.history.replaceState(
            {},
            document.title,
            window.location.pathname
          );
        }
      };

      handleCheckoutReturn();
    }

    if (
      billingStatus ===
        "success" &&
      isStripePopup
    ) {
      const handleSuccessfulPopupReturn = async () => {
        try {
          renderPopupMessage(
            "Payment submitted successfully",
            "Syncing your subscription. Please wait."
          );

          if (sessionId) {
            await syncCheckoutSession(
              sessionId
            );
          }

          localStorage.setItem(
            "agentbook_stripe_checkout_success",
            String(
              Date.now()
            )
          );

          renderPopupMessage(
            "Subscription updated",
            "This window will close automatically."
          );

          window.setTimeout(() => {
            window.close();
          }, 800);
        } catch (err) {
          console.error(
            "Stripe checkout popup sync error:",
            err
          );

          localStorage.setItem(
            "agentbook_stripe_checkout_sync_error",
            err.message ||
            "Failed to sync checkout"
          );

          renderPopupMessage(
            "Payment submitted",
            "We could not refresh your subscription automatically. Please close this window and refresh Settings."
          );
        }
      };

      handleSuccessfulPopupReturn();

      return;
    }

    if (
      billingStatus ===
        "cancelled" &&
      isStripePopup
    ) {
      try {
        localStorage.setItem(
          "agentbook_stripe_checkout_cancelled",
          String(
            Date.now()
          )
        );
      } catch {
        // Ignore localStorage failures.
      }

      window.setTimeout(() => {
        window.close();
      }, 500);

      return;
    }

    const handleStorageEvent = (event) => {
      if (
        event.key ===
        "agentbook_stripe_checkout_success"
      ) {
        localStorage.removeItem(
          "agentbook_stripe_checkout_success"
        );

        handleCheckoutSuccessRefresh();
      }

      if (
        event.key ===
        "agentbook_stripe_checkout_cancelled"
      ) {
        clearCheckoutPoll();

        setCheckoutMessage(
          "Checkout was cancelled."
        );

        setSubscriptionSaving(false);
      }

      if (
        event.key ===
        "agentbook_stripe_checkout_sync_error"
      ) {
        clearCheckoutPoll();

        setCheckoutMessage(
          event.newValue ||
          "Payment submitted. Please refresh Settings."
        );

        setSubscriptionSaving(false);
      }
    };

    window.addEventListener(
      "storage",
      handleStorageEvent
    );

    return () => {
      window.removeEventListener(
        "storage",
        handleStorageEvent
      );

      clearCheckoutPoll();
    };
  }, []);

  const handleStartStripeCheckout = async () => {
    try {
      if (
        !selectedSubscriptionPlanId ||
        !selectedSubscriptionPlan
      ) {
        alert(
          "Please select an upgrade plan."
        );

        return;
      }

      if (
        normalizeBoolean(
          selectedSubscriptionPlan
            .is_custom_pricing
        )
      ) {
        alert(
          "This plan requires custom pricing. Please contact support."
        );

        return;
      }

      const selectedPlanName =
        selectedSubscriptionPlan
          ?.plan_name ||
        "the selected plan";

      const confirmed = window.confirm(
        `Continue with ${selectedPlanName} at ${selectedPlanPriceLabel}?`
      );

      if (!confirmed) return;

      setSubscriptionSaving(true);
      setSubscriptionError("");

      setCheckoutMessage(
        "Updating your subscription..."
      );

      const token = getToken();

      if (!token) {
        throw new Error(
          "You must be logged in."
        );
      }

      const res = await fetch(
        `${API_BASE_URL}/api/stripe-billing/current/change-plan`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({
            subscription_plan_id:
              selectedSubscriptionPlanId,
            billing_frequency:
              selectedBillingFrequency
          })
        }
      );

      const data = await res.json();

      if (!res.ok) {
        throw new Error(
          data.message ||
          "Failed to update subscription"
        );
      }

      if (
        data.requires_checkout &&
        data.checkout_url
      ) {
        window.location.href =
          data.checkout_url;

        return;
      }

      setCheckoutMessage(
        "Your subscription was updated successfully."
      );

      setShowUpgradeOptions(false);
      setSelectedSubscriptionPlanId("");

      await loadSubscriptionSettings();
    } catch (err) {
      console.error(
        "Update Stripe subscription error:",
        err
      );

      setSubscriptionError(
        err.message ||
        "Failed to update subscription"
      );

      setCheckoutMessage("");

      alert(
        err.message ||
        "Failed to update subscription"
      );
    } finally {
      setSubscriptionSaving(false);
    }
  };

  return (
    <div className="sales-section-card sales-section-subscription">
      <div className="sales-section-head">
        <h2>Billing</h2>

        <p>
          View your current plan, manage billing, and review available upgrades.
        </p>
      </div>

      {subscriptionError && (
        <div
          className="profile-empty-card"
          style={{
            marginBottom: "14px",
            color: "#b91c1c"
          }}
        >
          {subscriptionError}
        </div>
      )}

      {checkoutMessage && (
        <div
          className="profile-empty-card"
          style={{
            marginBottom: "14px",
            color: "#475569"
          }}
        >
          {checkoutMessage}
        </div>
      )}

      <div className="sales-form-fields">
        <div className="sales-field">
          <label>Current Plan</label>

          <input
            value={currentPlanSummary}
            readOnly
          />
        </div>

        <div className="sales-field">
          <label>Billing Status</label>

          <input
            value={
              currentSubscription
                ?.billing_status ||
              "Not available"
            }
            readOnly
          />
        </div>

        <div className="sales-field">
          <label>Billing Frequency</label>

          <input
            value={
              currentSubscription
                ?.billing_frequency ||
              "Not available"
            }
            readOnly
          />
        </div>

        <div className="sales-field">
          <label>Paid Thru Date</label>

          <input
            value={formatDateOnly(
              currentSubscription
                ?.renewal_date
            )}
            readOnly
          />
        </div>

        <div className="sales-field">
          <label>Next Payment Due</label>

          <input
            value={formatDateOnly(
              currentSubscription
                ?.next_invoice_date
            )}
            readOnly
          />
        </div>
      </div>

      <div
        style={{
          marginTop: "14px",
          display: "flex",
          gap: "12px",
          alignItems: "center",
          flexWrap: "wrap"
        }}
      >
        {showConnectBillingButton ? (
          <button
            type="button"
            className="sales-primary-btn"
            onClick={
              handleConnectBilling
            }
            disabled={
              subscriptionSaving ||
              subscriptionLoading
            }
          >
            {subscriptionSaving
              ? "Opening Checkout..."
              : "Connect Billing"}
          </button>
        ) : (
          showBillingPortalButton && (
            <button
              type="button"
              className="sales-primary-btn"
              onClick={
                handleOpenBillingPortal
              }
              disabled={
                billingPortalLoading ||
                subscriptionLoading
              }
            >
              {billingPortalLoading
                ? "Opening Billing..."
                : "Manage Billing"}
            </button>
          )
        )}

        {hasUpgradeOptions ? (
          <button
            type="button"
            className="settings-card-link"
            onClick={() =>
              setShowUpgradeOptions(
                (previous) =>
                  !previous
              )
            }
            disabled={
              subscriptionLoading ||
              subscriptionSaving ||
              billingPortalLoading
            }
            style={{
              border: "none",
              background: "transparent",
              padding: 0,
              cursor: "pointer"
            }}
          >
            {showUpgradeOptions
              ? "Hide upgrade options"
              : "Upgrade Plan"}
          </button>
        ) : (
          <span className="settings-muted-text">
            No upgrade plans available.
          </span>
        )}
      </div>

      {showUpgradeOptions &&
        hasUpgradeOptions && (
          <div
            style={{
              marginTop: "16px"
            }}
          >
            <div className="sales-form-fields">
              <div className="sales-field">
                <label>Upgrade Plan</label>

                <select
                  value={
                    selectedSubscriptionPlanId
                  }
                  onChange={(event) =>
                    setSelectedSubscriptionPlanId(
                      event.target.value
                    )
                  }
                  disabled={
                    subscriptionSaving
                  }
                >
                  <option value="">
                    Select an upgrade plan
                  </option>

                  {upgradeEligiblePlans.map(
                    (plan) => {
                      const monthlyLabel =
                        normalizeBoolean(
                          plan.is_custom_pricing
                        )
                          ? "Custom pricing"
                          : `${formatPlanPrice(
                              plan.monthly_price
                            )}/mo`;

                      const annualLabel =
                        normalizeBoolean(
                          plan.is_custom_pricing
                        )
                          ? "Custom pricing"
                          : `${formatPlanPrice(
                              plan.annual_price
                            )}/yr`;

                      return (
                        <option
                          key={plan.id}
                          value={plan.id}
                        >
                          {plan.plan_name}
                          {" — "}
                          {monthlyLabel}
                          {" or "}
                          {annualLabel}
                        </option>
                      );
                    }
                  )}
                </select>
              </div>

              <div className="sales-field">
                <label>
                  Billing Frequency
                </label>

                <select
                  value={
                    selectedBillingFrequency
                  }
                  onChange={(event) =>
                    setSelectedBillingFrequency(
                      event.target.value
                    )
                  }
                  disabled={
                    subscriptionSaving
                  }
                >
                  <option value="monthly">
                    Monthly
                  </option>

                  <option value="annual">
                    Annual
                  </option>
                </select>
              </div>
            </div>

            {selectedSubscriptionPlan && (
              <div
                className="profile-empty-card"
                style={{
                  marginTop: "12px",
                  padding: "12px 14px"
                }}
              >
                <strong>
                  {
                    selectedSubscriptionPlan
                      .plan_name
                  }
                </strong>

                <span
                  style={{
                    marginLeft: "6px",
                    color: "#64748b"
                  }}
                >
                  will be billed at{" "}
                  {
                    selectedPlanPriceLabel
                  }{" "}
                  through Stripe.
                </span>
              </div>
            )}

            <div
              style={{
                marginTop: "12px"
              }}
            >
              <button
                type="button"
                className="sales-primary-btn"
                onClick={
                  handleStartStripeCheckout
                }
                disabled={
                  subscriptionSaving ||
                  !selectedSubscriptionPlan
                }
              >
                {subscriptionSaving
                  ? "Updating..."
                  : "Continue"}
              </button>
            </div>
          </div>
        )}
    </div>
  );
}

export default SubscriptionPlanSettingsCard;